import React, { useState, useEffect } from 'react';
import { IconButton } from '@mui/material';
import FavoriteIcon from '@mui/icons-material/Favorite';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useParams } from 'react-router-dom';
import './Posts.css';
import axios from 'axios';
import { connect } from 'react-redux';
const convertToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = error => reject(error);
  });
};

function mapStateToProps (state: any) {
  return { action: state };
}

interface Post {
  id: string;
  vetId: string;
  text: string;
  imageUrl: string;
  likes: number;
  isLiked: boolean;
  createdAt: string;
}

interface PostResponse {
  postId?: string;
  id?: string;
  vetId: string;
  text?: string;
  body?: string;
  imageUrl?: string;
  urlImg?: string;
  likes?: number;
  totalLikes?: number;
  isLiked?: boolean;
  createdAt: string;
}

interface ApiResponse {
  post?: PostResponse;
  posts?: PostResponse[];
}

const Posts: React.FC = (props: any) => {
  const { id: vetId } = useParams();
  const [posts, setPosts] = useState<Post[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newPostText, setNewPostText] = useState('');
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [menuOpenPostId, setMenuOpenPostId] = useState<string | null>(null);
  const [deleteConfirmPostId, setDeleteConfirmPostId] = useState<string | null>(null);
  const token = JSON.parse(localStorage.getItem('token') || '');

  const fetchPosts = async () => {
    try {
      const url = `${process.env.REACT_APP_URL}/posts?vetId=${vetId}`;
      const response = await axios.get<ApiResponse>(url, { headers: { Authorization: 'Bearer ' + token } });
      console.log('Fetched posts:', response.data);

      if (!response.data.posts) {
        console.error('No posts array in response:', response.data);
        return;
      }

      const fetchedPosts: Post[] = response.data.posts
        .map((post: PostResponse) => {
          const postId = post.postId || post.id;
          if (!postId) {
            console.error('Post missing ID:', post);
            return null;
          }
          return {
            id: postId,
            vetId: post.vetId,
            text: post.text || post.body || '',
            imageUrl: post.imageUrl || post.urlImg || '',
            likes: post.likes || post.totalLikes || 0,
            isLiked: Boolean(post.isLiked),
            createdAt: post.createdAt
          };
        })
        .filter((post): post is Post => post !== null);

      console.log('Processed posts:', fetchedPosts);
      setPosts(fetchedPosts);
    } catch (error) {
      console.error('Error fetching posts:', error);
    }
  };

  useEffect(() => {
    fetchPosts();
  }, []);

  const handleImageChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      const convertedFile = await convertToBase64(file);
      setSelectedImage(file);
      setPreviewUrl(convertedFile);
    }
  };

  const handleCreatePost = async () => {
    if (!newPostText.trim() && !selectedImage) return;

    try {
      let postData;
      if (selectedImage) {
        const convertedFile = await convertToBase64(selectedImage);
        postData = {
          vetId: vetId || '',
          text: newPostText,
          image: convertedFile,
          title: newPostText
        };
      } else {
        postData = {
          vetId: vetId || '',
          text: newPostText,
          title: newPostText,
          image: ''
        };
      }

      const response = await axios.post(`${process.env.REACT_APP_URL}/posts`, postData, {
        headers: { Authorization: 'Bearer ' + token }
      });

      console.log('Create post response:', response.data);

      if (response.data && response.data.post) {
        const newPost: Post = {
          id: response.data.post.postId || response.data.post.id,
          vetId: response.data.post.vetId,
          text: response.data.post.text || response.data.post.body,
          imageUrl: response.data.post.imageUrl || response.data.post.urlImg,
          likes: response.data.post.likes || response.data.post.totalLikes || 0,
          isLiked: Boolean(response.data.post.isLiked),
          createdAt: response.data.post.createdAt
        };

        if (!newPost.id) {
          console.error('Created post is missing ID:', response.data);
          return;
        }

        console.log('Adding new post to state:', newPost);
        setPosts(currentPosts => [newPost, ...currentPosts]);
      } else {
        console.error('Invalid post data in response:', response.data);
      }

      setNewPostText('');
      setSelectedImage(null);
      setPreviewUrl(null);
      setIsModalOpen(false);
    } catch (error) {
      console.error('Error creating post:', error);
    }
  };

  const handleMenuToggle = (e: React.MouseEvent, postId: string) => {
    e.stopPropagation(); // Stop event from bubbling up
    console.log('Toggle menu for post:', postId);
    setMenuOpenPostId(current => current === postId ? null : postId);
  };

  const handlePreDeletePost = (e: React.MouseEvent, postId: string) => {
    e.stopPropagation(); // Stop event from bubbling up
    console.log('Opening delete confirmation for post:', postId);
    setDeleteConfirmPostId(postId);
    setMenuOpenPostId(null);
  };

  const handleLikePost = async (postId: string) => {
    try {
      console.log('Liking post:', postId);
      const post = posts.find(p => String(p.id) === String(postId));

      if (!post) {
        console.error('Post not found for liking:', postId);
        return;
      }
      let response;

      if (post.isLiked) {
        const action = 'DELETE';
        response = await axios({
          method: action,
          url: `${process.env.REACT_APP_URL}/likePost/${postId}`,
          headers: { Authorization: 'Bearer ' + token }
        });
      } else {
        const action = 'POST';
        response = await axios({
          method: action,
          url: `${process.env.REACT_APP_URL}/likePost/like/${postId}`,
          headers: { Authorization: 'Bearer ' + token }
        });
      }

      if (response.status === 200 || response.status === 201) {
        setPosts(currentPosts =>
          currentPosts.map(p =>
            String(p.id) === String(postId)
              ? { ...p, likes: post.isLiked ? p.likes - 1 : p.likes + 1, isLiked: !p.isLiked }
              : p
          )
        );
      }
    } catch (error) {
      console.error('Error liking post:', error);
    }
  };

  const handleDeletePost = async (postId: string) => {
    try {
      console.log('Deleting post:', postId);
      const response = await axios.delete(`${process.env.REACT_APP_URL}/posts`, {
        headers: { Authorization: 'Bearer ' + token },
        params: { postId }
      });

      if (response.status === 204) {
        setPosts(currentPosts => currentPosts.filter(post => String(post.id) !== String(postId)));
        setDeleteConfirmPostId(null);
      }
    } catch (error) {
      console.error('Error deleting post:', error);
    }
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));

    if (diffInHours < 1) return 'Hace un momento';
    if (diffInHours < 24) return `Hace ${diffInHours} horas`;
    if (diffInHours < 48) return 'Ayer';
    return date.toLocaleDateString();
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Element;
      if (!target.closest('.menu-button') && !target.closest('.menu-popup')) {
        setMenuOpenPostId(null);
      }
    };

    if (menuOpenPostId) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [menuOpenPostId]);
  return (
    <div className="container-posts">
      <div className="posts-feed-header">
        Posts
        {props.action.user.credenciales.id === vetId && (
          <button className="post-button" onClick={() => setIsModalOpen(true)}>
            Crear nuevo post
          </button>
        )}
      </div>
      {isModalOpen && (
        <div className="modal">
          <div className="modal-content">
            <h2 className="texto-informacion">Crear nuevo post</h2>
            <textarea
              className="post-text-input"
              placeholder="Escribe un comentario..."
              value={newPostText}
              onChange={(e) => setNewPostText(e.target.value)}
            />
            <div className="post-buttons-container">
              <label htmlFor="post-image" className="post-button" style={{ cursor: 'pointer' }}>
                Subir imagen
                <input
                  type="file"
                  id="post-image"
                  accept="image/*"
                  onChange={handleImageChange}
                  style={{ display: 'none' }}
                />
              </label>
            </div>
            {previewUrl && (
              <img
                src={previewUrl}
                alt="Preview"
                className="post-image-preview"
              />
            )}
            <button
              className="post-button"
              onClick={handleCreatePost}
              disabled={!newPostText.trim() || !selectedImage}
            >
              Compartir
            </button>
            <button
              className="confirm-button-delete"
              onClick={() => setIsModalOpen(false)}
            >
              Cancelar
            </button>
          </div>
        </div>
      )}

      {deleteConfirmPostId && (
        <div className="confirm-modal">
          <div className="confirm-modal-content">
            <h3 className="confirm-modal-title">Eliminar post</h3>
            <p className="confirm-modal-text">¿Estás seguro de querer eliminar este post? Esta acción no se puede deshacer.</p>
            <div className="confirm-modal-buttons">
              <button
                className="confirm-button confirm-button-delete"
                onClick={() => handleDeletePost(deleteConfirmPostId)}
              >
                Eliminar
              </button>
              <button
                className="confirm-button confirm-button-cancel"
                onClick={() => setDeleteConfirmPostId(null)}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="posts-feed">
        {posts.map((post) => (
          <article key={post.id} className="post-card">
            <div className="post-header">
              {post.vetId === vetId && (
                <div style={{ position: 'relative' }}>
                  <IconButton
                    className="menu-button"
                    onClick={(e) => handleMenuToggle(e, String(post.id))}
                  >
                    <MoreVertIcon />
                  </IconButton>
                  {menuOpenPostId === String(post.id) && (
                    <div className="menu-popup">
                      <button
                        className="menu-item"
                        onClick={(e) => handlePreDeletePost(e, String(post.id))}
                      >
                        Eliminar Post
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="post-image-container">
              <img
                src={post.imageUrl}
                alt="Post"
                className="post-image"
              />
            </div>

            <div className="post-actions">
              <IconButton
                className={`action-button ${post.isLiked ? 'active' : ''}`}
                onClick={() => handleLikePost(String(post.id))}
              >
                <FavoriteIcon color={post.isLiked ? 'error' : 'inherit'} />
              </IconButton>
            </div>

            <div className="likes-count">
              {post.likes} likes
            </div>

            <p className="post-text">{post.text}</p>

            <div className="post-timestamp">
              {formatTimestamp(post.createdAt)}
            </div>
          </article>
        ))}
      </div>
    </div>
  );
};

export default connect(mapStateToProps)(Posts);
